import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, InputField } from "@mimber/forms"
import { Button, message, Typography } from "@mimber/ui-web"
import {
  forgotPasswordSchema,
  mapCognitoError,
  resetUserPassword,
} from "@mimber/auth"
import type {
  ForgotPasswordFormProps,
  ForgotPasswordFormData,
} from "./ForgotPasswordForm.types"

const { Paragraph } = Typography

export const ForgotPasswordForm: React.FunctionComponent<
  ForgotPasswordFormProps
> = ({ onSuccess, onError }) => {
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  })

  const handleSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    try {
      await resetUserPassword(data.email)
      message.success("Password reset code sent! Check your email.")
      onSuccess?.(data.email)
    } catch (error) {
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
      onError?.(error as Error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="mb-6 text-center">
        <Paragraph>
          Enter your email address and we'll send you a code to reset your
          password.
        </Paragraph>
      </div>

      <Form form={form} onSubmit={handleSubmit}>
        <InputField
          name="email"
          type="email"
          label="Email"
          placeholder="Enter your email"
          required
        />
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          className="w-full"
        >
          Send Reset Code
        </Button>
      </Form>
    </>
  )
}
